import React, { useRef, useEffect, useState } from 'react';
import { Wand2 } from 'lucide-react';

interface DrawingCanvasProps {
  onSketchComplete: (imageData: string, bounds: {x: number, y: number, width: number, height: number}) => void;
  isActive: boolean;
}

const DrawingCanvas: React.FC<DrawingCanvasProps> = ({ onSketchComplete, isActive }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [buttonPosition, setButtonPosition] = useState<{ left: number; top: number } | null>(null);
  const lastPos = useRef<{x: number, y: number} | null>(null);
  
  // Track bounding box of the drawing
  const boundsRef = useRef({ minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const resize = () => {
      // Set to offsetWidth/Height to match the container exactly
      if (canvas.parentElement) {
        canvas.width = canvas.parentElement.offsetWidth;
        canvas.height = canvas.parentElement.offsetHeight;
      }
    };
    
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [isActive]);

  // Reset when deactivating draw mode
  useEffect(() => {
    if (!isActive) {
      setHasDrawn(false);
      setButtonPosition(null);
      clearCanvas();
    }
  }, [isActive]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
      boundsRef.current = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
      setHasDrawn(false);
      setButtonPosition(null);
    }
  };

  const updateBounds = (x: number, y: number) => {
    boundsRef.current.minX = Math.min(boundsRef.current.minX, x);
    boundsRef.current.minY = Math.min(boundsRef.current.minY, y);
    boundsRef.current.maxX = Math.max(boundsRef.current.maxX, x);
    boundsRef.current.maxY = Math.max(boundsRef.current.maxY, y);
    
    // Update button position when bounds change
    if (hasDrawn) {
      const spacing = 12;
      setButtonPosition({
        left: boundsRef.current.maxX + spacing,
        top: boundsRef.current.minY - spacing,
      });
    }
  };

  const startDrawing = (e: React.MouseEvent) => {
    if (!isActive) return;
    setIsDrawing(true);
    setHasDrawn(true); // Mark as drawn immediately to show UI if needed
    
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.beginPath();
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.moveTo(x, y);
        lastPos.current = { x, y };
        updateBounds(x, y);
        
        // Set initial button position
        const spacing = 12;
        setButtonPosition({
          left: x + spacing,
          top: y - spacing,
        });
      }
    }
  };

  const draw = (e: React.MouseEvent) => {
    if (!isDrawing || !isActive || !canvasRef.current || !lastPos.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.lineTo(x, y);
      ctx.stroke();
      lastPos.current = { x, y };
      updateBounds(x, y);
    }
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    // Do NOT auto-complete. Wait for user to click Transform.
  };

  const handleTransform = () => {
    if (!canvasRef.current || !hasDrawn) return;
    
    const b = boundsRef.current;
    
    // Check if anything was drawn
    if (b.minX === Infinity || b.maxX === -Infinity) {
       clearCanvas();
       return;
    }

    // Add some padding
    const padding = 20;
    const width = (b.maxX - b.minX) + (padding * 2);
    const height = (b.maxY - b.minY) + (padding * 2);
    const x = b.minX - padding;
    const y = b.minY - padding;

    // Create a temporary canvas to crop the image
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
    const ctx = tempCanvas.getContext('2d');

    if (ctx) {
      // Fill with white background (crucial for AI recognition)
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);

      // Draw the cropped area from the main canvas
      // source: main canvas, x, y, w, h
      // dest: temp canvas, 0, 0, w, h
      ctx.drawImage(
        canvasRef.current,
        x, y, width, height, 
        0, 0, width, height
      );

      const dataUrl = tempCanvas.toDataURL('image/png');
      
      onSketchComplete(dataUrl, {
        x: x < 0 ? 0 : x, 
        y: y < 0 ? 0 : y, 
        width: width > 0 ? width : 100, 
        height: height > 0 ? height : 100
      });
    }
    
    clearCanvas();
  };

  if (!isActive) return null;

  return (
    <div className="absolute inset-0 z-50 pointer-events-none">
      {/* Canvas Layer - pointer events enabled only here */}
      <canvas
        ref={canvasRef}
        className="w-full h-full cursor-crosshair pointer-events-auto"
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
      />

      {/* Transform Button - Only show when something is drawn, positioned at top-right of drawn area */}
      {hasDrawn && buttonPosition && (
        <div 
          className="absolute pointer-events-auto animate-in fade-in slide-in-from-top-2 duration-200"
          style={{
            left: `${buttonPosition.left}px`,
            top: `${buttonPosition.top}px`,
            transform: 'translateY(-100%)', // Position above the top-right corner
          }}
        >
          <button 
            onClick={handleTransform}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full shadow-xl flex items-center gap-2 font-bold text-sm transition-all duration-150 animate-pulse-slow hover:scale-110 active:scale-95"
            title="Transform sketch to components"
          >
            <Wand2 size={16} />
            Transform
          </button>
        </div>
      )}
    </div>
  );
};

export default DrawingCanvas;